"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Card } from "@/components/ui/Card";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import { Zap, AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";

export default function ReferralLoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Get referral code from URL
    const code = searchParams.get("ref");
    if (code) {
      setReferralCode(code);
    }

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const existing = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        setRecaptchaReady(true);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaReady(true);
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      let recaptchaToken = "";
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
        if (!window.grecaptcha) {
          setError("Security verification not ready. Please try again.");
          setLoading(false);
          return;
        }
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "login" });
      }

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, recaptchaToken }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Login failed");
        } catch {
          setError(`Login failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      console.log("[REFERRAL LOGIN] Response data:", {
        role: data.user.role,
        hasReferral: data.user.hasReferral,
        hasGarage: data.user.hasGarage,
        email: data.user.email,
      });

      // Store user in localStorage
      localStorage.setItem("user", JSON.stringify(data.user));
      setSuccess(true);

      // Intelligent redirect based on entry point and user roles
      if (data.user.hasReferral) {
        // Entry point: referral-login page → referrals is primary
        console.log("[REFERRAL LOGIN] Redirecting to /referrals (has referral program)");
        setTimeout(() => {
          router.push("/referrals");
        }, 1000);
      } else if (data.user.hasGarage) {
        // User logged in via referral page but doesn't have referral account
        // Show error and ask them to use garage login
        console.log("[REFERRAL LOGIN] Error: user has garage but no referral");
        setError("You don't have a referral account. Please use the garage login.");
        setSuccess(false);
      } else {
        // Referral accounts should stay inside the referral area.
        console.log("[REFERRAL LOGIN] Fallback: redirecting to /referrals");
        setTimeout(() => {
          router.push("/referrals");
        }, 1000);
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="bg-linear-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6 min-h-[70vh]">
        <div className="w-full max-w-md space-y-6">
          <Card className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-linear-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center text-white shadow-lg">
                  <Zap size={32} fill="currentColor" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome Back</h1>
              <p className="text-slate-600 text-sm">Login to your referral dashboard</p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="text-green-600 shrink-0" size={20} />
                <p className="text-green-700 text-sm font-medium">Login successful! Redirecting...</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="text-red-600 shrink-0" size={20} />
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="your@email.com"
                  disabled={loading}
                  autoFocus
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                    Password
                  </label>
                  <Link
                    href="/forgot-password"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading || success || !recaptchaReady}
                className="w-full py-3 bg-linear-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold rounded-lg transition-all disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <span className="inline-block mr-2">⏳</span>
                    Logging in...
                  </>
                ) : success ? (
                  "Redirecting..."
                ) : (
                  "Login to Dashboard"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-300"></div>
              <span className="text-xs text-slate-500 font-medium">NEW HERE?</span>
              <div className="flex-1 h-px bg-slate-300"></div>
            </div>

            {/* Signup Link */}
            <Link
              href={`/referral-signup${referralCode ? `?ref=${encodeURIComponent(referralCode)}` : ""}`}
              className="block w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg transition-colors text-center"
            >
              Create Account
            </Link>

            {/* Footer Links */}
            <p className="text-center text-slate-600 text-xs mt-6">
              Want to learn more?{" "}
              <Link href="/join-referrals" className="text-blue-600 hover:text-blue-700 font-semibold">
                About the program →
              </Link>
            </p>
          </Card>

          <p className="text-center text-slate-400 text-sm">
            Want to setup your own garage?{" "}
            <Link href="/signup" className="text-blue-300 font-semibold hover:text-blue-200 transition-colors">
              Garage dashboard
            </Link>
          </p>
        </div>
      </div>
    </PublicLayout>
  );
}
